// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { otAssert, toRoundedAmount } from '@shared/conversions'
import { logOT } from '@shared/loggingShared'
import { LytReservesWithWithdrawalsStateCTO } from '@shared/ReservePlanCTO'
import { ReserveActionPlanClass, ReservePlanModeEnum } from './ReserveActionPlanClass'
const DECIMAL_FACTOR = 10000
const DECIMAL_FACTOR_BI = BigInt(DECIMAL_FACTOR)
export interface LytReservesWithStatsTestOutput {
  poolName: string
  preAssetBalance: string

  prePercentReserves: number
  minBalanceAmount: string
  maxBalanceAmount: string
  depositNeededForMin: string
  withdrawNeededForMax: string
  preWithdrawRequestedOrAcceptedAmount: string
  retrievableAssetBalance: string
  actualDepositAmount: string
  actualWithdrawAmount: string
  defaultDepositAmount: string
  defaultWithdrawAmount: string
  defaultPercentReserves: number
  postAssetBalance: string
  postPercentReserves: number
  isLiquidityAsset: boolean
  depositPriority: number
  withdrawPriority: number
}
export class LytReservesClass {
  constructor(lytReservesCTO: LytReservesWithWithdrawalsStateCTO, reserveActionPlanClass: ReserveActionPlanClass) {
    this.lytReservesCTO = lytReservesCTO
    this._reserveActionPlanClass = reserveActionPlanClass
    this.isAdjusting = false

    const assetBalanceBI = BigInt(lytReservesCTO.currentAssetValue ?? lytReservesCTO.assetBalance)
    const postTotalReserves = reserveActionPlanClass.postTotalReserves

    logOT.info(LytReservesClass.name, {
      assetBalanceBI,
      postTotalReserves: postTotalReserves,
      DECIMAL_FACTOR_BI,
      DECIMAL_FACTOR,
    })

    const minBalanceAmount =
      BigInt(Math.round((lytReservesCTO.percentReservesMin * Number(postTotalReserves / 1000000n)) / 10000)) * 1000000n

    otAssert(Number(minBalanceAmount) % 10 != 9, 'minBalanceAmount % 10 != 9n')

    const maxBalanceAmount =
      BigInt(Math.round((lytReservesCTO.percentReservesMax * Number(postTotalReserves / 1000000n)) / 10000)) * 1000000n

    otAssert(Number(maxBalanceAmount) % 10 != 9, 'minBalanceAmount % 10 != 9n')

    const depositNeededAmount = assetBalanceBI < minBalanceAmount ? minBalanceAmount - assetBalanceBI : 0n
    const withdrawRequestNeededAmount = assetBalanceBI > maxBalanceAmount ? assetBalanceBI - maxBalanceAmount : 0n

    this.preAssetBalance = assetBalanceBI
    if (this.isLiquidityAsset) {
      this.preAssetBalance = this.preAssetBalance + this._reserveActionPlanClass.nativeLiquidityAsset.lytPoolBalance
    }
    this.preTokenBalance = BigInt(lytReservesCTO.tokenBalance)

    this.minBalanceAmount = minBalanceAmount
    this.maxBalanceAmount = maxBalanceAmount
    this.depositNeededForMin = depositNeededAmount
    this.withdrawNeededForMax = withdrawRequestNeededAmount

    this._actualDepositAmount = 0n
    this.actualWithdrawAmount = 0n
    this.defaultDepositAmount = 0n
    this.defaultWithdrawAmount = 0n
    this.defaultPercentReserves = this.prePercentReserves

    this.postTokenBalance = this.preTokenBalance

    logOT.info(LytReservesClass.name, {
      tag: 'constructor',
      ...this.testOutput,
    })
  }

  lytReservesCTO: LytReservesWithWithdrawalsStateCTO
  private _reserveActionPlanClass: ReserveActionPlanClass
  preAssetBalance: bigint
  preTokenBalance: bigint

  minBalanceAmount: bigint // percentReservesAfterActions * minBalancePercent
  maxBalanceAmount: bigint // percentReservesAfterActions * maxBalancePercent

  depositNeededForMin: bigint // total deposits to this reserve needed to meet balance range
  withdrawNeededForMax: bigint // total withdrawals from this reserve needed to meet balance range

  // POST ACTIONS
  private _actualDepositAmount: bigint // total native deposits to this reserve from plan
  actualWithdrawAmount: bigint // total native deposits to this reserve from plan

  defaultDepositAmount: bigint // total native deposits to this reserve from plan
  defaultWithdrawAmount: bigint // total native deposits to this reserve from plan
  defaultPercentReserves: number // balance in this reserve + pending deposit - pending withdraw / sum of all reserves (balances  + pending deposit - pending withdraw)
  isAdjusting: boolean

  postTokenBalance: bigint

  //  offChainDepositAmount: bigint // total off-chain deposits to this reserve from plan native + offchain = depositNeededAmount

  notify() {
    this._reserveActionPlanClass.notify()
  }

  get retrievableAssetBalance(): bigint {
    const retrievableAssetBalance = this.preAssetBalance - this.preWithdrawRequestedOrAcceptedAmount
    // console.log('retrievableAssetBalance', {
    //   retrievableAssetBalance,
    //   preAssetBalance: this.preAssetBalance,
    //   preWithdrawRequestedOrAcceptedAmount: this.preWithdrawRequestedOrAcceptedAmount,
    //   poolName: this.poolName,

    //   prePercentReserves: Math.round(this.prePercentReserves * 100) / 100,
    //   minBalanceAmount: toRoundedAmount(this.minBalanceAmount),
    //   maxBalanceAmount: toRoundedAmount(this.maxBalanceAmount),
    //   depositNeededForMin: toRoundedAmount(this.depositNeededForMin),
    //   withdrawNeededForMax: toRoundedAmount(this.withdrawNeededForMax),

    //   actualDepositAmount: toRoundedAmount(this.actualDepositAmount),
    //   actualWithdrawAmount: toRoundedAmount(this.actualWithdrawAmount),
    //   defaultDepositAmount: toRoundedAmount(this.defaultDepositAmount),
    //   defaultWithdrawAmount: toRoundedAmount(this.defaultWithdrawAmount),
    //   defaultPercentReserves: Math.round(this.defaultPercentReserves * 100) / 100,
    //   postAssetBalance: toRoundedAmount(this.postAssetBalance),
    //   postPercentReserves: Math.round(this.postPercentReserves * 100) / 100,
    //   isLiquidityAsset: this.isLiquidityAsset,
    //   depositPriority: this.depositPriority,
    //   withdrawPriority: this.withdrawPriority,
    // })

    return retrievableAssetBalance
  }

  get preWithdrawRequestedOrAcceptedAmount(): bigint {
    const withdrawRequestedOrAcceptedAmount = this.lytReservesCTO.withdrawActivityCTOs.reduce(
      (total, activity) => total + (activity.stage == 1 || activity.stage == 2 ? BigInt(activity.assets) : 0n),
      0n
    )
    return withdrawRequestedOrAcceptedAmount
  }
  get reserveActionPlanClass(): ReserveActionPlanClass {
    return this._reserveActionPlanClass
  }
  get id(): string {
    return this.lytReservesCTO.id
  }
  get depositPriority(): number {
    return this.lytReservesCTO.depositPriority
  }
  get withdrawPriority(): number {
    return this.lytReservesCTO.withdrawalPriority
  }
  get isLiquidityAsset(): boolean {
    return this.lytReservesCTO.isLiquidityAsset
  }

  get poolAddr(): string {
    return this.lytReservesCTO.poolAddr
  }
  get poolName(): string {
    return this.lytReservesCTO.poolName
  }
  get assetBalanceForDepositCalculation(): bigint {
    if (this.isLiquidityAsset) {
      return 0n
    }
    return this.preAssetBalance
  }
  get actualDepositAmount(): bigint {
    return this._actualDepositAmount
  }
  get prePercentReserves(): number {
    const _prePercentReserves =
      this.reserveActionPlanClass.preTotalReserves === 0n
        ? 0
        : Number((this.preAssetBalance * 100n * DECIMAL_FACTOR_BI) / this.reserveActionPlanClass.preTotalReserves) /
          DECIMAL_FACTOR

    return _prePercentReserves
  }
  get postPercentReserves(): number {
    const _postPercentReserves =
      this.reserveActionPlanClass.postTotalReserves === 0n
        ? 0
        : Number((this.postAssetBalance * 100n * DECIMAL_FACTOR_BI) / this.reserveActionPlanClass.postTotalReserves) /
          DECIMAL_FACTOR

    logOT.debug(LytReservesClass.name, {
      tag: 'postPercentReserves',
      postPercentReserves: _postPercentReserves,
      postAssetBalance: this.postAssetBalance,
      postTotalReserves: this.reserveActionPlanClass.postTotalReserves,
      isLiquidityAsset: this.isLiquidityAsset,
      preAssetBalance: this.preAssetBalance,
      actualDepositAmount: this._actualDepositAmount,
      actualWithdrawAmount: this.actualWithdrawAmount,
    })
    return _postPercentReserves
  }

  get postAssetBalance(): bigint {
    if (this.isLiquidityAsset) {
      if (this.reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.InflowMode) {
        return (
          this._actualDepositAmount -
          this.actualWithdrawAmount +
          this._reserveActionPlanClass.nativeLiquidityAsset.lytPoolBalance
        )
      } else if (this.reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.OutflowMode) {
        return this.preAssetBalance - this.reserveActionPlanClass.marketMakerRedeemOutstandingBalance
      } else {
        return this.preAssetBalance + this._actualDepositAmount - this.actualWithdrawAmount
      }
    } else {
      return (
        this.preAssetBalance +
        this._actualDepositAmount -
        this.actualWithdrawAmount -
        this.preWithdrawRequestedOrAcceptedAmount
      )
    }
  }

  set actualDepositAmount(value: bigint) {
    this._actualDepositAmount = value

    this.notify()

    logOT.info(LytReservesClass.name, {
      tag: 'set actualDepositAmount',
      poolName: this.poolName,
      value,
      postTotalReserves: this.reserveActionPlanClass.postTotalReserves,
      postPercentReserves: this.postPercentReserves,
      actualDepositAmount: this._actualDepositAmount,
    })
  }

  validate() {}

  get testOutput(): LytReservesWithStatsTestOutput {
    return {
      poolName: this.poolName,
      preAssetBalance: toRoundedAmount(this.preAssetBalance),
      prePercentReserves: Math.round(this.prePercentReserves * 100) / 100,
      minBalanceAmount: toRoundedAmount(this.minBalanceAmount),
      maxBalanceAmount: toRoundedAmount(this.maxBalanceAmount),
      depositNeededForMin: toRoundedAmount(this.depositNeededForMin),
      withdrawNeededForMax: toRoundedAmount(this.withdrawNeededForMax),
      preWithdrawRequestedOrAcceptedAmount: toRoundedAmount(this.preWithdrawRequestedOrAcceptedAmount),
      actualDepositAmount: toRoundedAmount(this.actualDepositAmount),
      actualWithdrawAmount: toRoundedAmount(this.actualWithdrawAmount),
      defaultDepositAmount: toRoundedAmount(this.defaultDepositAmount),
      defaultWithdrawAmount: toRoundedAmount(this.defaultWithdrawAmount),
      defaultPercentReserves: Math.round(this.defaultPercentReserves * 100) / 100,
      postAssetBalance: toRoundedAmount(this.postAssetBalance),
      postPercentReserves: Math.round(this.postPercentReserves * 100) / 100,
      isLiquidityAsset: this.isLiquidityAsset,
      depositPriority: this.depositPriority,
      withdrawPriority: this.withdrawPriority,
      retrievableAssetBalance: toRoundedAmount(this.retrievableAssetBalance),
    }
  }
  toString(): string {
    const structuredOutput = this.testOutput
    return JSON.stringify(structuredOutput, null, 2)
  }
  [Symbol.for('nodejs.util.inspect.custom')](): string {
    return this.toString()
  }
}
